/**
 * DocumentUploader — Config-driven document upload component
 *
 * Fetches doc type config by handler code and renders upload cards for each type.
 * Supports custom fields, multi-file, validation, and file management.
 *
 * Usage:
 *   <DocumentUploader handlerCd="CONTRACTOR_DOCS" refId={contractorId} refNm="contractor" />
 *   <DocumentUploader handlerCd="TENDER_PREBID" refId={tenderId} refNm="tender" readOnly />
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import { ListSkeleton } from '@/components/base/Skeleton';
import { documentConfigApi } from '../services/wmsService';
import type { DocumentProcessConfig, DocumentTypeConfig, DocumentUpload, CustomFieldConfig } from '../services/wmsService';

interface DocumentUploaderProps {
  handlerCd: string;
  refId: number;
  refNm: string;
  readOnly?: boolean;
}

export default function DocumentUploader({ handlerCd, refId, refNm, readOnly = false }: DocumentUploaderProps) {
  const [config, setConfig] = useState<DocumentProcessConfig | null>(null);
  const [documents, setDocuments] = useState<DocumentUpload[]>([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState<number | null>(null); // doc_type_cnfg_id being uploaded
  const [customFields, setCustomFields] = useState<Record<string, Record<string, string>>>({}); // { [doc_type_cnfg_id]: { field_key: value } }
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [pendingType, setPendingType] = useState<DocumentTypeConfig | null>(null);

  const loadData = useCallback(async () => {
    // Returning early without clearing `loading` left the panel stuck on
    // "Loading document configuration..." for ever whenever refId was not yet
    // resolved on first render.
    if (!refId) {
      setLoading(false);
      return;
    }
    setLoading(true);
    // Settle both independently: an unconfigured process (404 on the config) must
    // not also discard the documents call. dswms_doc_prcss_lst_t is unseeded, so
    // this is the normal path today, not an exceptional one.
    const [cfgRes, docsRes] = await Promise.allSettled([
      documentConfigApi.getConfig(handlerCd),
      documentConfigApi.getDocuments(handlerCd, refId)
    ]);
    if (cfgRes.status === 'fulfilled') {
      setConfig(cfgRes.value);
    } else {
      setConfig(null);
      console.warn(`DocumentUploader: no document-process config for '${handlerCd}'`);
    }
    setDocuments(docsRes.status === 'fulfilled' ? (docsRes.value || []) : []);
    setLoading(false);
  }, [handlerCd, refId]);

  useEffect(() => { loadData(); }, [loadData]);

  const getDocsByType = (docTypeCnfgId: number) =>
    documents.filter(d => d.doc_type_cnfg_id === docTypeCnfgId);

  const canUploadMore = (dt: DocumentTypeConfig) => {
    const count = getDocsByType(dt.doc_type_cnfg_id).length;
    if (!dt.multi_file_in && count >= 1) return false;
    if (dt.multi_file_in && dt.max_files_nu && count >= dt.max_files_nu) return false;
    return true;
  };

  const triggerUpload = (dt: DocumentTypeConfig) => {
    setPendingType(dt);
    // Set accept attribute based on config
    if (fileInputRef.current) {
      fileInputRef.current.accept = dt.accptd_frmts_tx || '';
      fileInputRef.current.click();
    }
  };

  const handleFileSelected = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !pendingType || !config) return;

    // Client-side validation: extension
    const ext = '.' + file.name.split('.').pop()?.toLowerCase();
    const allowed = (pendingType.accptd_frmts_tx || '').split(',').map(f => f.trim().toLowerCase());
    if (allowed.length > 0 && allowed[0] !== '' && !allowed.includes(ext)) {
      alert(`File format '${ext}' not allowed.\nAccepted: ${pendingType.accptd_frmts_tx}`);
      if (fileInputRef.current) fileInputRef.current.value = '';
      return;
    }

    // Client-side validation: size
    if (pendingType.max_file_sz_nu && file.size > pendingType.max_file_sz_nu) {
      const maxMB = (pendingType.max_file_sz_nu / (1024 * 1024)).toFixed(1);
      alert(`File size (${(file.size / (1024 * 1024)).toFixed(1)} MB) exceeds maximum of ${maxMB} MB`);
      if (fileInputRef.current) fileInputRef.current.value = '';
      return;
    }

    setUploading(pendingType.doc_type_cnfg_id);
    try {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('doc_prcss_id', String(config.doc_prcss_id));
      fd.append('doc_type_cnfg_id', String(pendingType.doc_type_cnfg_id));
      fd.append('ref_id', String(refId));
      fd.append('ref_nm', refNm);
      fd.append('doc_nm', pendingType.doc_type_nm);

      // Add custom field values — each one exactly once.
      //
      // doc_nu / issue_dt / expiry_dt are ordinary entries in cstm_flds_jn for any
      // document type that declares them (BG Number, Issue Date, Validity Date on a
      // bank guarantee), so the loop below already sends them. A second explicit
      // append used to follow "in case they were present", which put TWO values under
      // each name in the multipart body. Express parses a repeated field as an ARRAY,
      // so req.body.issue_dt arrived as ['2026-08-05','2026-08-05'] and pg tried to
      // write a Postgres array literal into a DATE column — the upload died with a
      // bare 500 that named neither the field nor the reason.
      const fields = customFields[pendingType.doc_type_cnfg_id];
      if (fields) {
        Object.entries(fields).forEach(([key, val]) => {
          if (val) fd.append(key, val);
        });
      }

      await documentConfigApi.uploadDocument(fd);

      // Clear custom fields for this type after upload
      setCustomFields(prev => {
        const next = { ...prev };
        delete next[pendingType.doc_type_cnfg_id];
        return next;
      });

      loadData();
    } catch (err: any) {
      const msg = err?.response?.data?.message || err.message || 'Upload failed';
      alert(msg);
    } finally {
      setUploading(null);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleDelete = async (docUpldId: number) => {
    if (!confirm('Delete this document? The file will be removed.')) return;
    try {
      await documentConfigApi.deleteDocument(docUpldId);
      loadData();
    } catch (e) {
      console.error('Delete failed:', e);
    }
  };

  const updateCustomField = (docTypeCnfgId: number, key: string, value: string) => {
    setCustomFields(prev => ({
      ...prev,
      [docTypeCnfgId]: { ...(prev[docTypeCnfgId] || {}), [key]: value }
    }));
  };

  const formatFileSize = (bytes: number | null) => {
    if (!bytes) return '';
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  if (loading) {
    return (
      <ListSkeleton rows={3} avatar={false} />
    );
  }

  if (!config) {
    return (
      <div className="text-center py-8 text-gray-400">
        <i className="ri-error-warning-line text-2xl mb-2"></i>
        <p className="text-sm">Document configuration not found for '{handlerCd}'</p>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      <input
        type="file"
        ref={fileInputRef}
        className="hidden"
        onChange={handleFileSelected}
      />

      {config.docTypes.map((dt) => {
        const docs = getDocsByType(dt.doc_type_cnfg_id);
        const canUpload = !readOnly && canUploadMore(dt);
        const isUploading = uploading === dt.doc_type_cnfg_id;
        const hasCustomFields = dt.cstm_flds_jn && dt.cstm_flds_jn.length > 0;
        const fields = customFields[dt.doc_type_cnfg_id] || {};

        return (
          <div key={dt.doc_type_cnfg_id} className="border border-gray-200 rounded-lg p-3">
            {/* Header */}
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-2">
                <i className={`${dt.icon_cd || 'ri-file-line'} text-blue-500`}></i>
                <span className="text-sm font-medium text-gray-700">{dt.doc_type_nm}</span>
                {dt.is_rqrd_in && (
                  <span className="px-1.5 py-0.5 text-[10px] font-medium bg-red-50 text-red-600 rounded">Required</span>
                )}
                {dt.multi_file_in && (
                  <span className="px-1.5 py-0.5 text-[10px] font-medium bg-gray-100 text-gray-500 rounded">
                    {docs.length}/{dt.max_files_nu}
                  </span>
                )}
              </div>
              {canUpload && !hasCustomFields && (
                <button
                  onClick={() => triggerUpload(dt)}
                  disabled={isUploading}
                  className="px-2.5 py-1 text-xs bg-blue-50 text-blue-600 rounded-lg hover:bg-blue-100 disabled:opacity-50 flex items-center gap-1"
                >
                  {isUploading ? (
                    <><i className="ri-loader-4-line animate-spin"></i> Uploading...</>
                  ) : (
                    <><i className="ri-upload-2-line"></i> Upload</>
                  )}
                </button>
              )}
            </div>

            {/* Format/size info */}
            <div className="text-[10px] text-gray-400 mb-2">
              {dt.accptd_frmts_tx && <span>Formats: {dt.accptd_frmts_tx}</span>}
              {dt.max_file_sz_nu && <span className="ml-2">Max: {formatFileSize(dt.max_file_sz_nu)}</span>}
            </div>

            {/* Custom fields form (shown when config has custom fields) */}
            {hasCustomFields && canUpload && (
              <div className="bg-gray-50 rounded-lg p-2.5 mb-2">
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 mb-2">
                  {(dt.cstm_flds_jn as CustomFieldConfig[]).map((cf) => (
                    <div key={cf.key}>
                      <label className="block text-[10px] text-gray-500 mb-0.5">
                        {cf.label}{cf.required && <span className="text-red-500 ml-0.5">*</span>}
                      </label>
                      <input
                        type={cf.type === 'date' ? 'date' : cf.type === 'number' ? 'number' : 'text'}
                        value={fields[cf.key] || ''}
                        onChange={(e) => updateCustomField(dt.doc_type_cnfg_id, cf.key, e.target.value)}
                        placeholder={cf.placeholder || cf.label}
                        className="w-full px-2 py-1 text-xs border rounded focus:ring-1 focus:ring-blue-300 focus:border-blue-400 outline-none"
                      />
                    </div>
                  ))}
                </div>
                {/* These fields are sent as part of the same multipart upload as the
                    file (see the FormData built in handleFileSelected) — there is no
                    metadata-only save endpoint, and a document row without a document
                    would be meaningless. Users reasonably looked for a Save button, so
                    say plainly that the button below is the save. */}
                <button
                  onClick={() => triggerUpload(dt)}
                  disabled={isUploading}
                  className="px-3 py-1 text-xs bg-blue-500 text-white rounded-lg hover:bg-blue-600 disabled:opacity-50 flex items-center gap-1"
                >
                  {isUploading ? (
                    <><i className="ri-loader-4-line animate-spin"></i> Uploading...</>
                  ) : (
                    <><i className="ri-upload-2-line"></i> Choose File &amp; Save</>
                  )}
                </button>
                <p className="text-[10px] text-gray-500 mt-1.5">
                  <i className="ri-information-line mr-0.5"></i>
                  These details are saved together with the file — pick a file to save them. They are not stored on their own.
                </p>
              </div>
            )}

            {/* Uploaded files list */}
            {docs.length > 0 && (
              <div className="space-y-1">
                {docs.map((d) => (
                  <div key={d.doc_upld_id} className="flex items-center justify-between bg-green-50 rounded px-2.5 py-1.5 group">
                    <div className="flex items-center gap-2 min-w-0">
                      <i className="ri-file-check-line text-green-600 text-sm shrink-0"></i>
                      <div className="min-w-0">
                        <div className="flex items-center gap-1.5">
                          <a
                            href={d.file_url || '#'}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-xs text-green-700 hover:underline truncate"
                            title={d.orgnl_file_nm || d.file_nm}
                          >
                            {d.orgnl_file_nm || d.file_nm}
                          </a>
                          {d.file_sz && (
                            <span className="text-[10px] text-gray-400 shrink-0">{formatFileSize(d.file_sz)}</span>
                          )}
                        </div>
                        {/* Show doc number, dates, custom data */}
                        <div className="flex items-center gap-2 text-[10px] text-gray-500">
                          {d.doc_nu && <span>#{d.doc_nu}</span>}
                          {d.issue_dt && <span>Issued: {d.issue_dt}</span>}
                          {d.expiry_dt && (
                            <span className={new Date(d.expiry_dt) < new Date() ? 'text-red-500' : ''}>
                              Exp: {d.expiry_dt}
                            </span>
                          )}
                          {d.cstm_data_jn && Object.keys(d.cstm_data_jn).length > 0 && (
                            Object.entries(d.cstm_data_jn)
                              .filter(([k]) => !['doc_nu', 'issue_dt', 'expiry_dt'].includes(k))
                              .map(([k, v]) => (
                                <span key={k}>{k}: {String(v)}</span>
                              ))
                          )}
                        </div>
                      </div>
                    </div>
                    {!readOnly && (
                      <button
                        onClick={() => handleDelete(d.doc_upld_id)}
                        className="text-red-400 hover:text-red-600 ml-2 opacity-0 group-hover:opacity-100 transition-opacity shrink-0"
                        title="Remove"
                      >
                        <i className="ri-close-line"></i>
                      </button>
                    )}
                  </div>
                ))}
              </div>
            )}

            {/* Empty state */}
            {docs.length === 0 && (
              <div className="text-[11px] text-gray-400 italic">
                No files uploaded{dt.is_rqrd_in ? ' (required)' : ''}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}
